import {
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  Typography,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { FC, useMemo } from "react";
import { gql } from "@shared/utils";
import {useMutation} from '@apollo/client/react';
import { CategoryData } from "../ProductOption";
import { GroupedOptionListItem } from "../../../utils/group";

const REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM = gql`
  mutation RemoveProductConfigurationListItem(
    $subCategoryIds: [String!]
    $productConfigurationId: String!
  ) {
    removeProductConfigurationListItem(
      input: { id: $productConfigurationId, subCategoryIds: $subCategoryIds }
    ) {
      _id
      OptionsList {
        SubCategory {
          name
          maxOptions
          _id
          Category {
            name
            _id
          }
          Options {
            name
            _id
            AllowList {
              subcategory
              options
            }
          }
        }
        options
      }
    }
  }
`;

export const RemoveIncompatibleCategories: FC<{
  open: boolean;
  productConfigurationId: string;
  incompatibleCategories: Array<{
    id: string;
    found: boolean;
  }>;
  requiredCategories: Array<{
    id: string;
    compatible: boolean;
    found: boolean;
    categoriesToRemove: string[];
  }>;
  categorys: CategoryData[];
  groupedOptions: GroupedOptionListItem[];
  onClose?: () => void;
  onRemoveComplete?: () => void;
}> = ({
  open,
  productConfigurationId,
  incompatibleCategories,
  requiredCategories,
  categorys,
  groupedOptions,
  onClose,
  onRemoveComplete,
}) => {
  const theme = useTheme();

  const [removeProductConfigurationListItem] = useMutation(
    REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM,
    {
      update: () => {
        onRemoveComplete && onRemoveComplete();
        onClose && onClose();
      },
    }
  );

  // Find all subcategories that need to be removed
  const allSubCategoriesToRemove = useMemo(() => {
    const result = new Map<
      string,
      {
        categoryName: string;
        subCategories: Array<{ id: string; name: string }>;
      }
    >();

    // For each incompatible category that is currently selected
    incompatibleCategories
      .filter((c) => c.found)
      .forEach((category) => {
        // Find the grouped options for this category
        const groupedItem = groupedOptions.find(
          (g) => g.category?._id === category.id
        );
        if (groupedItem) {
          result.set(category.id, {
            categoryName: groupedItem.category.name,
            subCategories: groupedItem.items
              .filter(
                (item): item is typeof item & {
                  SubCategory: NonNullable<(typeof item)["SubCategory"]>;
                } => Boolean(item.SubCategory?._id && item.SubCategory?.name)
              )
              .map((item) => ({
                id: item.SubCategory._id,
                name: item.SubCategory.name,
              })),
          });
        }
      });

    // Add categories from categoriesToRemove in required categories
    requiredCategories.forEach((reqCategory) => {
      if (reqCategory.categoriesToRemove) {
        reqCategory.categoriesToRemove.forEach((categoryId) => {
          const groupedItem = groupedOptions.find(
            (g) => g.category?._id === categoryId
          );
          if (groupedItem && !result.has(categoryId)) {
            result.set(categoryId, {
              categoryName: groupedItem.category.name,
              subCategories: groupedItem.items
                .filter(
                  (
                    item
                  ): item is typeof item & {
                    SubCategory: NonNullable<(typeof item)["SubCategory"]>;
                  } => Boolean(item.SubCategory?._id && item.SubCategory?.name)
                )
                .map((item) => ({
                  id: item.SubCategory._id,
                  name: item.SubCategory.name,
                })),
            });
          }
        });
      }
    });

    return result;
  }, [incompatibleCategories, groupedOptions, requiredCategories]);

  const hasIncompatibleRequired = false; // requiredCategories.some((c) => !c.compatible);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        Incompatible Categories
      </DialogTitle>
      <DialogContent>
        {(allSubCategoriesToRemove.size > 0 && (
          <>
            <Typography variant="subtitle1">
              The following categories and their components will be removed:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              {Array.from(allSubCategoriesToRemove.entries()).map(
                ([categoryId, data]) => (
                  <Box key={categoryId} sx={{ mb: 1 }}>
                    <Typography>• {data.categoryName}:</Typography>
                  </Box>
                )
              )}
            </Box>
          </>
        )) || (
          <>
            <CircularProgress size={24} color="inherit" />
          </>
        )}
        {hasIncompatibleRequired && (
          <>
            <Typography variant="subtitle1" color="error">
              This feature cannot be added because it requires incompatible
              categories:
            </Typography>
            <Box sx={{ ml: 2 }}>
              {requiredCategories
                .filter((c) => !c.compatible)
                .map((c) => {
                  const category = categorys.find((cat) => cat._id === c.id);
                  return (
                    <Typography key={c.id} color="error">
                      • {category?.name || c.id}
                    </Typography>
                  );
                })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {!hasIncompatibleRequired && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              // Get all subcategory IDs that need to be removed
              const subCategoryIds = Array.from(
                allSubCategoriesToRemove.values()
              ).flatMap((data) => data.subCategories.map((sub) => sub.id));
              removeProductConfigurationListItem({
                variables: {
                  productConfigurationId: productConfigurationId,
                  subCategoryIds,
                },
              }).then(() => {
                onRemoveComplete && onRemoveComplete();
              });
            }}
          >
            Remove and Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
